import React from 'react';
import { Grid, IconButton, TextField } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { css, keyframes } from '@emotion/react';
import { colors } from '@/styles';
import { PrimaryButton } from '@/styles/blocks';
import { Header1 } from '../blocks';
import ZipSection from './ZipSection';
import { PANEL_INSET, PANEL_SLIDE_MS, PANEL_WIDTH } from './constants';

// From just past the map's left edge, so it slides out from behind the subregions column.
const slideIn = keyframes`
    from {
        transform: translateX(calc(-100% - ${PANEL_INSET}px));
    }
    to {
        transform: translateX(0);
    }
`;

// Shared by Auto Assign and Regions. Optional per-page sections: `carriers` and `settings` (nodes).
// The name stays at the top and Save at the bottom; everything between scrolls as one.
const SubregionPanel = ({ subregion, subregions, callbacks, draft, onClose, carriers, settings }) => (
    <Grid
        container
        direction="column"
        css={css`
            position: absolute;
            width: ${PANEL_WIDTH}px;
            top: ${PANEL_INSET}px;
            left: ${PANEL_INSET}px;
            max-height: calc(100% - ${2 * PANEL_INSET}px);
            flex-wrap: nowrap;
            overflow: hidden;
            border: 1px solid ${colors.greys.border};
            border-radius: 6px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
            background: ${colors.white.primary};
            animation: ${slideIn} ${PANEL_SLIDE_MS}ms ease-out;
            @media (prefers-reduced-motion: reduce) {
                animation: none;
            }
        `}
    >
        <Grid
            container
            direction="row"
            css={css`
                flex: 0 0 auto;
                flex-wrap: nowrap;
                align-items: center;
                gap: 8px;
                padding: 10px 12px;
                background: ${colors.greens.primary};
            `}
        >
            <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Subregion name"
                value={draft.name}
                onChange={(e) => draft.setName(e.target.value)}
                inputProps={{ 'aria-label': 'subregion name' }}
                css={css`
                    .MuiInputBase-root {
                        background-color: ${colors.white.primary};
                        font-weight: 600;
                    }
                `}
            />
            <IconButton
                size="small"
                aria-label="close subregion panel"
                onClick={onClose}
                css={css`
                    width: 32px;
                    height: 32px;
                    flex-shrink: 0;
                    border-radius: 4px;
                    border: 1px solid ${colors.greys[3]};
                    background: ${colors.white.secondary};
                    color: ${colors.greys[3]};
                    &:hover {
                        background: ${colors.white.primary};
                    }
                `}
            >
                <CloseIcon
                    css={css`
                        font-size: 18px;
                    `}
                />
            </IconButton>
        </Grid>

        <Grid
            container
            direction="column"
            css={css`
                flex: 1 1 auto;
                flex-wrap: nowrap;
                min-height: 0;
                overflow-y: auto;
            `}
        >
            {settings ? (
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0 0 auto;
                        flex-wrap: nowrap;
                        border-top: 1px solid ${colors.greys.border};
                    `}
                >
                    {settings}
                </Grid>
            ) : null}

            {carriers}

            <ZipSection
                zips={draft.zips}
                subregions={subregions}
                subregionId={subregion?.subregion_id || null}
                callbacks={callbacks}
                onAdd={draft.addZipsToDraft}
                onRemove={draft.removeZipFromDraft}
            />
        </Grid>

        <Grid
            container
            css={css`
                flex: 0 0 auto;
                padding: 12px;
                border-top: 1px solid ${colors.greys.border};
                box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.06);
                background: ${colors.white.primary};
            `}
        >
            <PrimaryButton
                fullWidth
                onClick={draft.save}
                // New subregion: dirty means it has a name or zip, so Save can't insert an empty row.
                disabled={draft.saving || !draft.isDirty}
                css={css`
                    height: 40px;
                `}
            >
                <Header1>{draft.saving ? 'Saving…' : 'Save Subregion'}</Header1>
            </PrimaryButton>
        </Grid>
    </Grid>
);

export default SubregionPanel;
